import { useEffect } from 'react'
import { useMotionValue, type MotionValue } from 'framer-motion'
import type { SidepanelSide } from '@shared/sidepanel/side'

interface UseEdgeProximityOptions {
  /**
   * Fraction of screen width over which {@link EdgeProximity.proximity} ramps
   * from 0 (far) to 1 (at the active edge). `<= 0` disables proximity tracking
   * and pins it to 0.
   */
  zoneRatio: number
  /** Which screen edge to track proximity to (the sidepanel's anchored side). */
  side: SidepanelSide
  /**
   * When `false`, detaches the `mousemove` listener entirely — no `proximity`
   * updates. Use it to stop the global listener idle-spinning while the trigger
   * is dormant (e.g. panel open, or affordance disabled). Defaults to `true`.
   */
  enabled?: boolean
}

interface EdgeProximity {
  /** 0 → 1 across the configured edge zone. Pinned to 0 when disabled. */
  proximity: MotionValue<number>
}

/**
 * Shared edge proximity tracking for the sidepanel peek trigger and the
 * onboarding completion step: computes horizontal proximity to the panel's
 * screen edge. The output is a motion value, so consumers animate off it
 * without re-rendering on mouse move. The trigger itself stays pinned to
 * vertical center, so only horizontal proximity is tracked.
 */
export function useEdgeProximity({ zoneRatio, side, enabled = true }: UseEdgeProximityOptions): EdgeProximity {
  const proximity = useMotionValue(0)

  useEffect(() => {
    // Proximity stops updating when disabled or below the threshold, so reset it to 0.
    if (!enabled || zoneRatio <= 0) {
      proximity.set(0)
      return
    }

    const handleMouseMove = (e: MouseEvent) => {
      const zoneWidth = window.innerWidth * zoneRatio
      const distFromEdge = side === 'right' ? window.innerWidth - e.clientX : e.clientX
      proximity.set(Math.max(0, Math.min(1, 1 - distFromEdge / zoneWidth)))
    }

    window.addEventListener('mousemove', handleMouseMove, { passive: true })
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [enabled, zoneRatio, side, proximity])

  return { proximity }
}
